/**
 * Modelos CSAT alineados con el backend real (be_crmv2, módulo chatflow).
 *
 * Fuente de verdad verificada contra:
 *   - dto/ChatflowCsatConfigResponse.java   (GET/PUT /api/chatflows/{id}/csat-config)
 *   - dto/ChatflowCsatConfigRequest.java    (cuerpo del PUT)
 *   - dto/ChatflowCsatSubmitRequest.java    (POST /api/chatflows/runtime/conversations/{id}/csat)
 *   - dto/ChatflowCsatSubmitResponse.java
 *   - dto/ChatflowCsatMetricsResponse.java  (GET /api/chatflows/{id}/csat-metrics)
 *   - application/ChatflowCsatService.java  (validateConfig → valores válidos)
 *
 * La respuesta del backend es PLANA: no existe ningún envoltorio { chatflowId, config, ... }.
 */

/** Valores aceptados por ChatflowCsatService.validateConfig(). */
export type ChatflowCsatRatingScale = 'STARS_5' | 'EMOJI_3' | 'NUMERIC_10';
/**
 * OBSOLETO (backend V189): la encuesta se pide en CUALQUIER cierre de la conversación
 * mientras esté habilitada y activa, así que el backend guarda siempre 'ON_CLOSE' e ignora
 * lo que se le mande. Se conserva el tipo porque el campo sigue viajando en el contrato.
 */
export type ChatflowCsatTriggerMode = 'ON_CLOSE' | 'ON_AGENT_CLOSE' | 'ON_BOT_END';

/** Respuesta plana de GET/PUT /csat-config. */
export interface ChatflowCsatConfigResponse {
  id: number | null;
  chatflowId: number;
  enabled: boolean | null;
  triggerMode: ChatflowCsatTriggerMode | null;
  ratingScale: ChatflowCsatRatingScale | null;
  /** El backend devuelve null cuando no se ha configurado texto. */
  questionText: string | null;
  commentEnabled: boolean | null;
  /** Short en el backend; validado entre 1 y 10. */
  negativeThreshold: number | null;
  notifySupervisorOnNegative: boolean | null;
  supervisorUserId: number | null;
  sendByEmailIfWidgetClosed: boolean | null;
  active: boolean | null;
  datetimeCreated: string | null;
  datetimeUpdated: string | null;
}

/**
 * Cuerpo del PUT /csat-config. Solo estos 10 campos son escribibles.
 * El backend aplica campo a campo ignorando los null (applyConfigRequest),
 * así que enviar null NO borra un valor: lo deja como está.
 */
export interface ChatflowCsatConfigRequest {
  enabled: boolean | null;
  /** Obsoleto: el backend lo ignora y guarda siempre ON_CLOSE. No hace falta enviarlo. */
  triggerMode?: ChatflowCsatTriggerMode | null;
  ratingScale: ChatflowCsatRatingScale | null;
  questionText: string | null;
  commentEnabled: boolean | null;
  negativeThreshold: number | null;
  notifySupervisorOnNegative: boolean | null;
  supervisorUserId: number | null;
  sendByEmailIfWidgetClosed: boolean | null;
  active: boolean | null;
}

/** Request que envía el visitante al enviar su CSAT (widget público). */
export interface ChatflowCsatSubmitRequest {
  publicConversationToken: string;
  rating: number;
  comment?: string;
  /** Opcional: si se omite, el backend usa el ratingScale de la config. */
  ratingScale?: ChatflowCsatRatingScale;
}

/** Respuesta del POST de envío del widget. */
export interface ChatflowCsatSubmitResponse {
  id: number;
  conversationId: number;
  rating: number;
  ratingScale: string;
  comment: string | null;
  submittedAt: string;
}

/** Métricas CSAT. `ratingDistribution` es un Map<Integer,Long> → objeto, NO un array. */
export interface ChatflowCsatMetricsResponse {
  chatflowId: number;
  totalResponses: number;
  averageRating: number;
  responseRate: number;
  positiveCount: number;
  neutralCount: number;
  negativeCount: number;
  ratingDistribution: Record<string, number>;
  byAgent: ChatflowCsatAgentMetric[];
  byTeam: ChatflowCsatTeamMetric[];
  recentComments: ChatflowCsatRecentComment[];
}

export interface ChatflowCsatAgentMetric {
  userId: number;
  userName: string;
  count: number;
  average: number;
}

export interface ChatflowCsatTeamMetric {
  teamId: number;
  teamName: string;
  count: number;
  average: number;
}

export interface ChatflowCsatRecentComment {
  conversationId: number;
  rating: number;
  comment: string;
  submittedAt: string;
}

/**
 * Una respuesta CSAT concreta — GET /api/chatflows/{id}/csat-responses.
 *
 * Es lo que consulta el supervisor al abrir la notificación de CSAT negativo: necesita la
 * valoración y el comentario de UNA conversación, no un promedio (INC_CRM_019 (8) inc60).
 */
export interface ChatflowCsatResponseItem {
  id: number;
  conversationId: number;
  visitorName: string | null;
  rating: number;
  ratingScale: ChatflowCsatRatingScale | string | null;
  comment: string | null;
  /** true si cae en la banda negativa configurada (mismo umbral que la alerta). */
  negative: boolean;
  assignedAgentId: number | null;
  assignedAgentName: string | null;
  teamId: number | null;
  teamName: string | null;
  submittedAt: string;
}

export const CSAT_SCALE_LABELS: Record<ChatflowCsatRatingScale, string> = {
  STARS_5: '5 estrellas',
  EMOJI_3: '3 emojis (😞 😐 😊)',
  NUMERIC_10: 'Escala del 1 al 10'
};

export const CSAT_SCALE_DESCRIPTIONS: Record<ChatflowCsatRatingScale, string> = {
  STARS_5: 'El visitante elige entre 1 y 5 estrellas.',
  EMOJI_3: 'El visitante elige entre tres emojis que representan insatisfecho, neutral y satisfecho.',
  NUMERIC_10: 'El visitante elige un número del 1 al 10.'
};

/** Convierte el Map del backend en filas ordenadas para pintar la distribución. */
export function toRatingDistributionRows(
  distribution: Record<string, number> | null | undefined
): { rating: number; count: number }[] {
  if (!distribution) return [];
  return Object.keys(distribution)
    .map(key => ({ rating: Number(key), count: distribution[key] ?? 0 }))
    .filter(row => Number.isFinite(row.rating))
    .sort((a, b) => a.rating - b.rating);
}
